
interface Product {
  id: string;
  name: string;
  category: string;
  subcategory: string;
  manufacturer: string;
  region: string;
  price: number;
  unit: string;
  image: string;
  description: string;
  specs: string[];
  inStock: boolean;
  rating: number;
  reviews: number;
}

interface ProductCardProps {
  product: Product;
}

export function ProductCard({ product }: ProductCardProps) {
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ko-KR').format(price);
  };

  const getRegionName = (region: string) => {
    const regions: Record<string, string> = {
      busan: '부산',
      boryeong: '보령',
      mokpo: '목포',
      yeosu: '여수',
      tongyeong: '통영',
      pohang: '포항',
      jeju: '제주',
    };
    return regions[region] || region;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-amber-100/80 overflow-hidden hover:shadow-lg hover:shadow-amber-100/50 transition-all duration-300 group cursor-pointer">
      {/* Product Image */}
      <div className="relative w-full h-64 bg-amber-50 overflow-hidden">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
        />
        {!product.inStock && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="bg-red-500 text-white px-4 py-2 rounded-lg font-semibold text-sm whitespace-nowrap">
              품절
            </span>
          </div>
        )}
        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-amber-800 whitespace-nowrap">
          {product.subcategory}
        </div>
      </div>

      {/* Product Info */}
      <div className="p-5">
        {/* Manufacturer & Region */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-amber-700 font-medium bg-amber-50 px-2 py-1 rounded border border-amber-100 whitespace-nowrap">
            {product.manufacturer}
          </span>
          <span className="text-xs text-gray-500 flex items-center gap-1 whitespace-nowrap">
            <i className="ri-map-pin-line"></i>
            {getRegionName(product.region)}
          </span>
        </div>

        {/* Product Name */}
        <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 min-h-[3.5rem]">
          {product.name}
        </h3>

        {/* Rating */}
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center gap-1">
            <i className="ri-star-fill text-amber-400 text-sm"></i>
            <span className="text-sm font-semibold text-gray-900">{product.rating}</span>
          </div>
          <span className="text-xs text-gray-500 whitespace-nowrap">
            ({product.reviews}개 리뷰)
          </span>
        </div>

        {/* Specs Preview */}
        <div className="mb-4 space-y-1">
          {product.specs.slice(0, 2).map((spec, index) => (
            <p key={index} className="text-xs text-gray-600 flex items-start gap-1">
              <i className="ri-checkbox-circle-fill text-amber-500 text-sm mt-0.5"></i>
              <span className="line-clamp-1">{spec}</span>
            </p>
          ))}
        </div>

        {/* Price & CTA */}
        <div className="flex items-center justify-between pt-4 border-t border-amber-100/60">
          <div>
            <p className="text-xs text-gray-500 mb-1">가격</p>
            <p className="text-xl font-bold text-amber-700 whitespace-nowrap">
              {formatPrice(product.price)}원
              <span className="text-sm text-gray-500 font-normal ml-1">/ {product.unit}</span>
            </p>
          </div>
          <button className="px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:from-amber-400 hover:to-orange-400 transition-all text-sm font-medium whitespace-nowrap flex items-center gap-2 shadow-sm shadow-amber-200 cursor-pointer">
            <span>상세보기</span>
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </div>
    </div>
  );
}
